import { useState, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Form/Select"
import { useSubscriptions, type Subscription } from "@/hooks/useSubscriptions"
import { UnbackedAccessPanel } from "@/components/UnbackedAccessPanel"
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  GiftIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"
import { getEffectiveStatus, getTrialDaysRemaining } from "@/utils/subscription-helpers"

export function Subscriptions() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [hasTeamFilter, setHasTeamFilter] = useState<boolean | undefined>(undefined)

  const { data, isLoading, error } = useSubscriptions({
    page,
    pageSize,
    status: statusFilter,
    has_team: hasTeamFilter,
  })

  const handleSubscriptionClick = useCallback(
    (subscriptionId: number) => {
      navigate(`/subscriptions/${subscriptionId}`)
    },
    [navigate]
  )

  const getStatusBadge = (subscription: Subscription) => {
    // Use effective status to properly detect trials via trial_end_date
    const effectiveStatus = getEffectiveStatus(subscription)
    const trialDaysRemaining = getTrialDaysRemaining(subscription.trial_end_date)

    if (subscription.cancel_at_period_end) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <XCircleIcon className="h-3 w-3 mr-1" />
          Cancelling
        </span>
      )
    }

    switch (effectiveStatus) {
      case "active":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </span>
        )
      case "trial":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            Trial ({trialDaysRemaining}d left)
          </span>
        )
      case "cancelled":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Cancelled
          </span>
        )
      case "past_due":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            Past Due
          </span>
        )
      case "unpaid":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            Unpaid
          </span>
        )
      default:
        return <span className="text-sm">{subscription.status}</span>
    }
  }

  const columns = useMemo<ColumnDef<Subscription>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <span className="font-mono text-sm text-secondary">#{row.original.id}</span>
        ),
      },
      {
        accessorKey: "creator",
        header: "Customer",
        cell: ({ row }) => (
          <div className="cursor-pointer">
            {row.original.creator ? (
              <>
                {/* On a comp this column holds the granting ADMIN, not a buyer —
                    `creator_id` is who made the grant. Labelling that "Customer"
                    reads as though an admin bought the team's plan. */}
                {row.original.comp_reason && (
                  <p className="text-xs text-secondary uppercase tracking-wide">Granted by</p>
                )}
                <p className="font-medium text-primary hover:underline underline-offset-2">
                  {row.original.creator.name || "Unnamed User"}
                </p>
                <p className="text-sm text-secondary">{row.original.creator.email}</p>
              </>
            ) : (
              <p className="text-sm text-secondary italic">No creator</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "team",
        header: "Team / Organization",
        cell: ({ row }) => (
          <div>
            {row.original.organization_public_id ? (
              <div>
                <p className="font-medium">{row.original.organization_name}</p>
                <p className="text-xs text-secondary">Organization</p>
              </div>
            ) : row.original.team ? (
              <div className="flex items-center gap-1">
                <p className="font-medium text-primary">{row.original.team.name}</p>
                <span className="text-xs text-secondary">({row.original.team.public_id})</span>
              </div>
            ) : row.original.entity_id === null ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700">
                Pending
              </span>
            ) : (
              <span className="text-sm text-secondary italic">No team</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "plan",
        header: "Plan",
        cell: ({ row }) => (
          <div>
            {row.original.plan ? (
              <>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">{row.original.plan.name}</p>
                  {/* A comp bills $0, so nothing in the money columns distinguishes
                      it from a paying customer. Say so where the plan is read. */}
                  {row.original.comp_reason && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                      title={row.original.comp_reason}
                    >
                      <GiftIcon className="h-3 w-3 mr-1" />
                      Comp
                    </span>
                  )}
                </div>
                <p className="text-xs text-secondary capitalize">{row.original.plan.tier} tier</p>
              </>
            ) : (
              <span className="text-sm text-secondary italic">Unknown plan</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => getStatusBadge(row.original),
      },
      {
        accessorKey: "stripe_subscription_id",
        header: "Stripe ID",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-secondary">
            {row.original.stripe_subscription_id || "—"}
          </span>
        ),
      },
      {
        accessorKey: "current_period_end",
        header: "Period End",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {row.original.current_period_end
              ? new Date(row.original.current_period_end).toLocaleDateString()
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm text-secondary">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
    ],
    []
  )

  const handleResetFilters = () => {
    setStatusFilter(undefined)
    setHasTeamFilter(undefined)
    setPage(1)
  }

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Subscriptions</h1>
            <p className="text-secondary">View and manage subscription billing records</p>
          </div>
        </div>

        <UnbackedAccessPanel />

        {/* Filters */}
        <div className="flex items-center gap-4 bg-surface p-4 rounded-lg border border-border">
          <FunnelIcon className="h-5 w-5 text-secondary" />
          <div className="flex items-center gap-3 flex-1">
            <Select
              value={statusFilter || "all"}
              onValueChange={(value) => {
                setStatusFilter(value === "all" ? undefined : value)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={
                hasTeamFilter === undefined ? "all" : hasTeamFilter ? "with_team" : "without_team"
              }
              onValueChange={(value) => {
                setHasTeamFilter(value === "all" ? undefined : value === "with_team" ? true : false)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All subscriptions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subscriptions</SelectItem>
                <SelectItem value="with_team">With team</SelectItem>
                <SelectItem value="without_team">Orphaned</SelectItem>
              </SelectContent>
            </Select>

            {(statusFilter || hasTeamFilter !== undefined) && (
              <Button variant="outline" onClick={handleResetFilters} size="sm">
                Reset filters
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : data?.subscriptions?.length === 0 ? (
          <EmptyState
            icon={<CreditCardIcon className="h-12 w-12" />}
            title="No subscriptions found"
            description={
              statusFilter || hasTeamFilter !== undefined
                ? "No subscriptions match the selected filters."
                : "No subscriptions have been created yet."
            }
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={data?.subscriptions || []}
              searchKey="creator.email"
              onRowClick={(subscription) => handleSubscriptionClick(subscription.id)}
            />

            {data?.pagination && (
              <div className="flex items-center justify-between text-sm text-secondary">
                <div>
                  Showing {(data.pagination.page - 1) * data.pagination.pageSize + 1} to{" "}
                  {Math.min(data.pagination.page * data.pagination.pageSize, data.pagination.total)}{" "}
                  of {data.pagination.total} subscriptions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {data.pagination.page} of {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  )
}
